import { useRef, useState } from 'react';

import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useSelfServeSignup } from 'api/useSelfServeSignup';
import { AxiosError } from 'axios';
import { EEventsType, ESignUpMethod } from 'constants/enums';
import { useLogin } from 'hooks/useLogin';
import { authActions } from 'store/authSlice';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import { EToasterStatus, showToast } from 'utils/showToast';
import { z } from 'zod';

import { SIGN_UP_FORM_SCHEMA } from './constants';

export const useSignUp = () => {
  const { addNewUser, addNewUserGoogle } = useSelfServeSignup();
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const { saveLoginDetails } = useLogin();
  const dispatch = useDispatch();

  const handlePostLogin = (data: any) => {
    const loginPayload: any = { userId: data.userId };
    if (data?.token) loginPayload.authJwt = data.token;
    dispatch(authActions.login(loginPayload));

    saveLoginDetails({
      email: data.email,
      firstName: data.userName,
      lastName: '',
      userId: data.userId,
      isSession: false,
      token: data?.token,
      role: data?.role,
      typeId: data?.typeId,
      projects: data?.projects,
      portals: data?.portals,
      version: data?.version,
      company: data?.company,
      companyDisplayName: data?.companyDisplayName,
      isOnboarding: data?.isOnboarding
    });

    const navigationPath =
      (!data.projects || data.projects.length === 0) &&
      (!data.portals || data.portals.length === 0)
        ? '/init-project'
        : '/';
    navigate(navigationPath, { replace: true });
  };

  const getGoogleUserProfile = async (token: string) => {
    const response = await fetch(
      'https://www.googleapis.com/oauth2/v3/userinfo',
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.json();
  };

  const trackSignUpCompleted = (data: any) => {
    trackMixpanelEvent(EEventsType.DASHBOARD_SIGNUP_COMPLETED, {
      ...data
    });
  };

  const handleGoogleRegistration = async (loginData: {
    loginToken: string;
  }) => {
    try {
      const profile = await getGoogleUserProfile(loginData.loginToken || '');
      const recaptchaToken = await recaptchaRef.current?.executeAsync();

      if (!recaptchaToken) {
        showToast({
          message: 'Please complete the reCAPTCHA verification',
          status: EToasterStatus.ERROR
        });
        return;
      }
      recaptchaRef.current?.reset();

      addNewUserGoogle.mutate(
        {
          data: {
            token: loginData.loginToken || '',
            name: profile.name || ''
          },
          recaptchaToken
        },
        {
          onSuccess: (data) => {
            trackSignUpCompleted({user_id: data.userId, email: data.email, method: ESignUpMethod.GOOGLE});
            handlePostLogin(data);
          },
          onError: (error) => {
            setIsSuccess(false);
            showToast({
              message: 'Google registration failed. Please try again.',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError)?.response?.data as {
                    [key: string]: any;
                  }
                )?.message ?? 'Unknown error occurred'
            });
          }
        }
      );
    } catch (error) {
      setIsSuccess(false);
      showToast({
        message: 'Google registration failed. Please try again.',
        status: EToasterStatus.ERROR
      });
    }
  };

  const handleRegistration = async (
    data: z.infer<typeof SIGN_UP_FORM_SCHEMA>
  ) => {
    const recaptchaToken = await recaptchaRef.current?.executeAsync();

    if (!recaptchaToken) {
      showToast({
        message: 'Please complete the reCAPTCHA verification',
        status: EToasterStatus.ERROR
      });
      return;
    }

    recaptchaRef.current?.reset();

    addNewUser.mutate(
      { data, recaptchaToken },
      {
        onSuccess: () => {
          setIsSuccess(true);
          trackSignUpCompleted({email: data.email, method: ESignUpMethod.EMAIL});
          showToast({
            message: 'Registration successful!',
            status: EToasterStatus.SUCCESS,
            description:
              'You will be redirected to the confirmation page shortly.'
          });
          setTimeout(() => {
            navigate(
              `/sign-up/confirmation?email=${encodeURIComponent(data.email)}`
            );
          }, 1500);
        },
        onError: (error) => {
          setIsSuccess(false);
          showToast({
            message: 'Registration failed. Please try again.',
            status: EToasterStatus.ERROR,
            description:
              ((error as AxiosError)?.response?.data as { [key: string]: any })
                ?.message ?? 'Unknown error occurred'
          });
        }
      }
    );
  };

  const isLoading =
    isSuccess || addNewUser.isLoading || addNewUserGoogle.isLoading;

  return {
    recaptchaRef,
    isLoading,
    handleGoogleRegistration,
    handleRegistration,
    handlePostLogin
  };
};
